import { useCallback, useRef, useState, type CSSProperties, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Icon } from '../../components/icons/Icon'
import { ColumnHeaderInfo } from './cells/ColumnHeaderInfo'
import { boundsForColumn, clampWidth, useColumnResize } from './useColumnResize'
import { isEditableSafeField } from './inlineEditSafety'
import type { ColumnDef, Density, SortSpec } from './types'
import './DataGrid.css'

export interface DataGridProps<T> {
  columns: ColumnDef<T>[]
  rows: T[]
  getRowId: (row: T) => string
  density: Density
  sort: SortSpec[]
  onSortChange: (next: SortSpec[]) => void
  selection: Set<string>
  onSelectionChange: (next: Set<string>) => void
  onRowActivate?: ((row: T) => void) | undefined
  renderRowActions?: ((row: T) => ReactNode) | undefined
  ariaLabel: string
  /**
   * Persisted per-user-per-list resize widths (IB-27 S3, design §4b) — `Record<columnId, number>`.
   * `DataGrid` never persists to `localStorage` on its own — that stays the caller's job (via
   * `onColumnWidthChange`, below). Omit this (or pass `{}`) for a screen that hasn't retrofitted
   * `localStorage` persistence yet: `DataGrid` falls back to its OWN internal width state in that
   * case, so resize still fully works for the current session (survives re-renders, just not a
   * reload) on EVERY list — not only the two screens that also persist it across reloads (AC3). A
   * key for a column not in the current column set, or a non-finite/non-positive value (a
   * corrupted/stale persisted blob), is ignored and falls back to `column.width` — never a crash,
   * never a zero/negative width.
   */
  columnWidths?: Record<string, number> | undefined
  /** Fires once a drag/keyboard resize completes — wire this to persist to `localStorage`
   * (together with `columnWidths`) for a screen that wants resize to survive a reload; optional —
   * `DataGrid` tracks the current value internally either way (see `columnWidths` above). */
  onColumnWidthChange?: ((columnId: string, width: number) => void) | undefined
}

const ROW_HEIGHT: Record<Density, number> = { compact: 32, comfortable: 44 }
const SELECTION_COL_WIDTH = 40
const ACTIONS_COL_WIDTH = 44

/**
 * `docs/design-system.md` §8: "Any list/grid ≥ 200 rows renders virtualized (windowed)" — an
 * already-loaded, larger in-memory row set, not server-side infinite-scroll (IB-27 S3 design §4d).
 * Below this, `DataGrid` renders exactly as it always has — a plain `.map()`, zero windowing
 * overhead, zero visual change (AC8 failure path / AC11 regression gate).
 */
export const VIRTUALIZE_THRESHOLD = 200
/** How many off-screen rows to keep mounted above/below the visible window. */
const OVERSCAN = 8

function pinnedLeftOffsets(
  columnIds: ReadonlyArray<string>,
  selectable: boolean,
  widthOf: (columnId: string) => number,
): Map<string, number> {
  const offsets = new Map<string, number>()
  let running = selectable ? SELECTION_COL_WIDTH : 0
  for (const id of columnIds) {
    offsets.set(id, running)
    running += widthOf(id)
  }
  return offsets
}

interface EditState {
  rowId: string
  columnId: string
  value: string
  status: 'editing' | 'saving'
  error: string | null
}

/**
 * THE single render-path choke point (Verifier F2): a column only ever enters edit mode if it
 * BOTH opts in (`ColumnDef.editable`) AND its id is on the positive `EDITABLE_SAFE_FIELD_IDS`
 * allow-list — refused here regardless of what any `ColumnDef` claims, not merely by a hand-
 * maintained denylist test. See `inlineEditSafety.ts`.
 */
function isEditable<T>(column: ColumnDef<T>): boolean {
  return Boolean(column.editable) && isEditableSafeField(column.id)
}

/**
 * The universal grid body (design-system.md §3.2 grid a11y contract):
 * `role="grid"`, cell-level roving tabindex, arrow-key navigation, Space to
 * select a row, Enter to activate the focused cell's primary action.
 *
 * IB-27 S3 adds three hardening capabilities on top of that unchanged contract: column resize
 * (drag handle + `Shift+ArrowLeft/Right`, `useColumnResize.ts`), inline edit for `editable`
 * (safe-fields-only, opt-in, absent-by-default) columns, and row virtualization once
 * `rows.length >= VIRTUALIZE_THRESHOLD` (headless `@tanstack/react-virtual` — `DataGrid` keeps
 * owning `role="grid"`/keyboard nav/ARIA row semantics; see design §4a).
 */
export function DataGrid<T>({
  columns,
  rows,
  getRowId,
  density,
  sort,
  onSortChange,
  selection,
  onSelectionChange,
  onRowActivate,
  renderRowActions,
  ariaLabel,
  columnWidths,
  onColumnWidthChange,
}: DataGridProps<T>) {
  const rowHeight = ROW_HEIGHT[density]
  const hasActions = Boolean(renderRowActions)
  // Column 0 is the selection checkbox; then each data column; then (if present) the actions column.
  const totalCols = 1 + columns.length + (hasActions ? 1 : 0)
  const [focused, setFocused] = useState({ row: -1, col: 0 }) // row -1 = header row
  const [editState, setEditState] = useState<EditState | null>(null)
  const cellRefs = useRef(new Map<string, HTMLElement>())
  const scrollRef = useRef<HTMLDivElement>(null)

  // Fallback "uncontrolled" resize-width store for a caller that hasn't wired `columnWidths` (a
  // real bug, found only by running the real Playwright e2e against real Chromium — jsdom's lack
  // of CSS layout/paint hid it): without this, `useColumnResize`'s live-drag preview vanishes the
  // instant the mouse is released (its internal `drag` state clears on mouseup) for EVERY screen
  // except the two retrofit screens that persist `columnWidths` themselves — the resize would
  // visibly snap back to the default width on every mouseup. `DataGrid` now always has SOME place
  // to land the post-drag value — the caller's `columnWidths` prop if given, else this internal
  // state — so resize genuinely works for the current session on every list, per AC1, not just the
  // two screens that also persist it across a reload (AC3).
  const [internalWidths, setInternalWidths] = useState<Record<string, number>>({})
  const persistedWidths = columnWidths ?? internalWidths
  const handleColumnWidthChange = useCallback(
    (id: string, width: number) => {
      setInternalWidths((prev) => ({ ...prev, [id]: width }))
      onColumnWidthChange?.(id, width)
    },
    [onColumnWidthChange],
  )
  const resize = useColumnResize(handleColumnWidthChange)

  const widths = new Map<string, number>(
    columns.map((column) => {
      const bounds = boundsForColumn(column)
      const raw = persistedWidths[column.id]
      const persisted = typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? clampWidth(raw, bounds) : column.width
      return [column.id, resize.widthFor(column.id, persisted)]
    }),
  )
  const widthOf = (id: string) => widths.get(id) ?? 0
  const pinnedOffsets = pinnedLeftOffsets(
    columns.map((c) => c.id),
    true,
    widthOf,
  )

  const shouldVirtualize = rows.length >= VIRTUALIZE_THRESHOLD
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: useCallback(() => scrollRef.current, []),
    estimateSize: useCallback(() => rowHeight, [rowHeight]),
    overscan: OVERSCAN,
    // `enabled: false` below the threshold means the virtualizer does no scroll-element
    // measurement/observation at all — genuinely "zero windowing overhead" for the common
    // <200-row case (design §4a), not just an unused-output no-op.
    enabled: shouldVirtualize,
  })

  const setCellRef = useCallback(
    (key: string) => (el: HTMLElement | null) => {
      if (el) cellRefs.current.set(key, el)
      else cellRefs.current.delete(key)
    },
    [],
  )

  function focusCell(row: number, col: number) {
    const clampedRow = Math.max(-1, Math.min(row, rows.length - 1))
    const clampedCol = Math.max(0, Math.min(col, totalCols - 1))
    setFocused({ row: clampedRow, col: clampedCol })
    const key = `${clampedRow}:${clampedCol}`
    const existing = cellRefs.current.get(key)
    if (existing) {
      existing.focus()
      return
    }
    // Virtualized: the target row isn't mounted yet — scroll it into view first (design §4a /
    // AC9), then focus once it renders.
    if (shouldVirtualize && clampedRow >= 0) {
      virtualizer.scrollToIndex(clampedRow, { align: 'auto' })
    }
    // A TIME-based deadline, not a fixed retry count: `@tanstack/react-virtual`'s own scroll
    // reconciliation (`scheduleScrollReconcile`) can take several rAF cycles to settle a large
    // jump against a REAL scroll container (confirmed against real Chromium via Playwright — a
    // fixed small retry count gave up before the target row ever mounted, silently dropping focus
    // to `<body>` once the row-0 cell that originally held focus was itself virtualized away).
    // 1 second is a generous, still-bounded ceiling — never a perceptible hang for a real user.
    const deadline = Date.now() + 1000
    const attemptFocus = () => {
      const el = cellRefs.current.get(key)
      if (el) {
        el.focus()
        return
      }
      if (Date.now() < deadline) requestAnimationFrame(attemptFocus)
    }
    requestAnimationFrame(attemptFocus)
  }

  function toggleRowSelection(rowId: string) {
    const next = new Set(selection)
    if (next.has(rowId)) next.delete(rowId)
    else next.add(rowId)
    onSelectionChange(next)
  }

  function toggleAllSelection() {
    if (selection.size === rows.length) {
      onSelectionChange(new Set())
    } else {
      onSelectionChange(new Set(rows.map(getRowId)))
    }
  }

  // --- inline edit (safe fields only — design §4c; guarded fields never carry `editable`, see
  // DataGrid.guardedFieldsNeverEditable.test.tsx) ---
  function beginEdit(row: T, column: ColumnDef<T>) {
    // Defense in depth: refuse even if a caller reaches this function directly — the render path
    // (renderCellContent/aria-readonly/the cell onClick/the Enter-key handler below) already
    // gates on the same `isEditable` check, but this function does not trust its callers either.
    if (!column.editable || !isEditable(column)) return
    setEditState({ rowId: getRowId(row), columnId: column.id, value: column.editable.getValue(row), status: 'editing', error: null })
  }

  function cancelEdit() {
    setEditState(null)
  }

  async function commitEdit() {
    if (!editState || editState.status === 'saving') return
    const row = rows.find((r) => getRowId(r) === editState.rowId)
    const column = columns.find((c) => c.id === editState.columnId)
    if (!row || !column?.editable || !isEditable(column)) {
      setEditState(null)
      return
    }
    const { value } = editState
    setEditState((prev) => (prev ? { ...prev, status: 'saving', error: null } : prev))
    try {
      await column.editable.commit(row, value)
      setEditState(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not save this change.'
      setEditState((prev) => (prev ? { ...prev, status: 'editing', error: message } : prev))
    }
  }

  function handleEditKeyDown(event: KeyboardEvent<HTMLInputElement | HTMLSelectElement>) {
    // Stop this from also reaching the grid-level arrow/Enter navigation handler below.
    event.stopPropagation()
    if (event.key === 'Enter') {
      event.preventDefault()
      void commitEdit()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      cancelEdit()
    }
  }

  // --- column resize (drag handle + keyboard — design §4b) ---
  function handleResizeHandleMouseDown(event: MouseEvent<HTMLDivElement>, column: ColumnDef<T>) {
    event.preventDefault()
    event.stopPropagation()
    resize.beginDrag(column.id, event.clientX, widthOf(column.id), boundsForColumn(column))
  }

  function handleHeaderKeyDown(event: KeyboardEvent<HTMLElement>, column: ColumnDef<T>) {
    if (column.disabledReason) return
    const handled = resize.handleHeaderKeyboard(column.id, widthOf(column.id), boundsForColumn(column), event.key, event.shiftKey)
    if (handled) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  function handleGridKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const { row, col } = focused
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        focusCell(row + 1, col)
        break
      case 'ArrowUp':
        event.preventDefault()
        focusCell(row - 1, col)
        break
      case 'ArrowRight':
        event.preventDefault()
        focusCell(row, col + 1)
        break
      case 'ArrowLeft':
        event.preventDefault()
        focusCell(row, col - 1)
        break
      case 'Home':
        event.preventDefault()
        // WAI-ARIA APG grid pattern: `Home` -> first cell of the CURRENT row; `Ctrl+Home` -> first
        // cell of the WHOLE grid (crosses rows — the deterministic virtualization scroll-into-view
        // path, design §4a/AC9; see focusCell's scrollToIndex branch).
        if (event.ctrlKey || event.metaKey) {
          focusCell(0, 0)
        } else {
          focusCell(row, 0)
        }
        break
      case 'End':
        event.preventDefault()
        // `End` -> last cell of the CURRENT row; `Ctrl+End` -> last cell of the WHOLE grid.
        if (event.ctrlKey || event.metaKey) {
          focusCell(rows.length - 1, totalCols - 1)
        } else {
          focusCell(row, totalCols - 1)
        }
        break
      case ' ':
        if (row >= 0) {
          event.preventDefault()
          toggleRowSelection(getRowId(rows[row]!))
        }
        break
      case 'Enter': {
        if (row >= 0) {
          const column = col >= 1 && col <= columns.length ? columns[col - 1] : undefined
          if (column && isEditable(column)) {
            beginEdit(rows[row]!, column)
            break
          }
          const target = cellRefs.current.get(`${row}:${col}`)
          const widget = target?.querySelector<HTMLElement>('a,button')
          if (widget) {
            widget.click()
          } else {
            onRowActivate?.(rows[row]!)
          }
        }
        break
      }
      default:
        break
    }
  }

  function sortDirectionFor(columnId: string) {
    return sort.find((s) => s.columnId === columnId)?.direction
  }

  function cycleSort(columnId: string) {
    const index = sort.findIndex((s) => s.columnId === columnId)
    if (index === -1) {
      onSortChange([...sort, { columnId, direction: 'asc' }])
      return
    }
    const current = sort[index]!
    if (current.direction === 'asc') {
      const next = [...sort]
      next[index] = { columnId, direction: 'desc' }
      onSortChange(next)
      return
    }
    onSortChange(sort.filter((s) => s.columnId !== columnId))
  }

  const allSelected = rows.length > 0 && selection.size === rows.length
  const someSelected = selection.size > 0 && !allSelected

  function renderCellContent(row: T, column: ColumnDef<T>, rowId: string) {
    // Render-path guard (Verifier F2): an editor is NEVER rendered for a column that fails
    // `isEditable`, even if `column.editable` is set — the positive allow-list, not the presence
    // of the capability, is what decides. See `inlineEditSafety.ts`.
    if (!column.editable || !isEditable(column)) return column.render(row)
    const isEditing = editState?.rowId === rowId && editState.columnId === column.id
    if (!isEditing) return column.render(row)
    const saving = editState.status === 'saving'
    const inputProps = {
      className: column.editable.options ? 'iw-select' : 'iw-input',
      style: { width: '100%', padding: '2px 6px', fontSize: 'var(--iw-size-sm)' } satisfies CSSProperties,
      value: editState.value,
      disabled: saving,
      'aria-invalid': Boolean(editState.error),
      onKeyDown: handleEditKeyDown,
      onClick: (event: MouseEvent) => event.stopPropagation(),
      autoFocus: true,
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        {column.editable.options ? (
          <select
            {...inputProps}
            onChange={(event) => setEditState((prev) => (prev ? { ...prev, value: event.target.value } : prev))}
          >
            {column.editable.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            {...inputProps}
            onChange={(event) => setEditState((prev) => (prev ? { ...prev, value: event.target.value } : prev))}
          />
        )}
        {saving ? (
          <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-subtle)' }}>Saving…</span>
        ) : editState.error ? (
          <span role="alert" style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-danger)' }}>
            {editState.error}
          </span>
        ) : null}
      </div>
    )
  }

  function renderRow(row: T, rowIndex: number, styleOverride?: CSSProperties) {
    const rowId = getRowId(row)
    const selected = selection.has(rowId)
    return (
      <div
        key={rowId}
        role="row"
        aria-rowindex={rowIndex + 2}
        aria-selected={selected}
        className="iw-grid-row"
        style={{
          height: rowHeight,
          background: selected ? 'var(--iw-accent-subtle)' : undefined,
          cursor: onRowActivate ? 'pointer' : undefined,
          ...styleOverride,
        }}
        onClick={
          onRowActivate
            ? (event) => {
                // Mouse-activation counterpart to the keyboard Enter handling above
                // (handleGridKeyDown). Skip when the click originated on an interactive
                // descendant (a link cell, the selection checkbox, a row-action button, or an
                // inline-edit input/select — its own onClick already stopped propagation) — that
                // control's own handler already owns the click; this only fires for plain-text
                // cells (e.g. Branch/Contact rows, which have no detail page and no in-cell link —
                // "row-click → edit-modal only", IB-48 design §4.2).
                const target = event.target as HTMLElement
                if (target.closest('a, button, input, select, label')) return
                onRowActivate(row)
              }
            : undefined
        }
      >
        <div
          role="gridcell"
          aria-colindex={1}
          className="iw-grid-cell iw-grid-cell--pinned-left"
          style={{ width: SELECTION_COL_WIDTH, left: 0 }}
          tabIndex={focused.row === rowIndex && focused.col === 0 ? 0 : -1}
          ref={setCellRef(`${rowIndex}:0`)}
        >
          <input type="checkbox" aria-label="Select row" tabIndex={-1} checked={selected} onChange={() => toggleRowSelection(rowId)} />
        </div>
        {columns.map((column, colIndex) => {
          const gridColIndex = colIndex + 1
          const isEditingThisCell = editState?.rowId === rowId && editState.columnId === column.id
          const editable = isEditable(column)
          return (
            <div
              key={column.id}
              role="gridcell"
              aria-colindex={gridColIndex + 1}
              aria-readonly={editable ? 'false' : 'true'}
              aria-busy={isEditingThisCell && editState?.status === 'saving' ? 'true' : undefined}
              className={'iw-grid-cell' + (pinnedOffsets.has(column.id) && column.pinnable ? ' iw-grid-cell--pinned-left' : '')}
              style={{
                width: widthOf(column.id),
                left: column.pinnable ? pinnedOffsets.get(column.id) : undefined,
                textAlign: column.align === 'end' ? 'right' : 'left',
              }}
              tabIndex={focused.row === rowIndex && focused.col === gridColIndex ? 0 : -1}
              ref={setCellRef(`${rowIndex}:${gridColIndex}`)}
              onClick={
                editable && !isEditingThisCell
                  ? (event) => {
                      event.stopPropagation()
                      beginEdit(row, column)
                    }
                  : undefined
              }
            >
              {renderCellContent(row, column, rowId)}
            </div>
          )
        })}
        {hasActions ? (
          <div
            role="gridcell"
            aria-colindex={totalCols}
            className="iw-grid-cell iw-grid-cell--pinned-right"
            style={{ width: ACTIONS_COL_WIDTH, right: 0 }}
            tabIndex={focused.row === rowIndex && focused.col === totalCols - 1 ? 0 : -1}
            ref={setCellRef(`${rowIndex}:${totalCols - 1}`)}
          >
            {renderRowActions?.(row)}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div
      role="grid"
      ref={scrollRef}
      aria-label={ariaLabel}
      aria-rowcount={rows.length + 1}
      aria-colcount={totalCols}
      className="iw-grid"
      onKeyDown={handleGridKeyDown}
    >
      <div role="row" aria-rowindex={1} className="iw-grid-header-row" style={{ height: rowHeight }}>
        <div
          role="columnheader"
          aria-colindex={1}
          className="iw-grid-header-cell iw-grid-cell--pinned-left"
          style={{ width: SELECTION_COL_WIDTH, left: 0 }}
        >
          <input
            type="checkbox"
            aria-label={allSelected ? 'Deselect all rows' : 'Select all rows'}
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected
              setCellRef('-1:0')(el)
            }}
            onChange={toggleAllSelection}
            tabIndex={focused.row === -1 && focused.col === 0 ? 0 : -1}
          />
        </div>
        {columns.map((column, colIndex) => {
          const gridColIndex = colIndex + 1
          const direction = sortDirectionFor(column.id)
          const disabled = Boolean(column.disabledReason) || !column.sortable
          const resizable = !column.disabledReason
          const width = widthOf(column.id)
          return (
            <div
              key={column.id}
              role="columnheader"
              aria-colindex={gridColIndex + 1}
              aria-sort={
                column.sortable
                  ? direction === 'asc'
                    ? 'ascending'
                    : direction === 'desc'
                      ? 'descending'
                      : 'none'
                  : undefined
              }
              className={
                'iw-grid-header-cell' + (pinnedOffsets.has(column.id) && column.pinnable ? ' iw-grid-cell--pinned-left' : '')
              }
              style={{
                width,
                left: column.pinnable ? pinnedOffsets.get(column.id) : undefined,
                textAlign: column.align === 'end' ? 'right' : 'left',
                position: 'relative',
              }}
              title={column.disabledReason}
            >
              {column.sortable ? (
                <button
                  type="button"
                  className="iw-grid-sort-btn"
                  disabled={disabled}
                  tabIndex={focused.row === -1 && focused.col === gridColIndex ? 0 : -1}
                  ref={setCellRef(`-1:${gridColIndex}`)}
                  onClick={() => cycleSort(column.id)}
                  onKeyDown={(event) => handleHeaderKeyDown(event, column)}
                >
                  <span>{column.header}</span>
                  <Icon name={direction === 'desc' ? 'chevron-down' : direction === 'asc' ? 'chevron-up' : 'chevrons-up-down'} size={12} />
                </button>
              ) : (
                <span
                  tabIndex={focused.row === -1 && focused.col === gridColIndex ? 0 : -1}
                  ref={setCellRef(`-1:${gridColIndex}`) as never}
                  onKeyDown={(event) => handleHeaderKeyDown(event, column)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: disabled ? 'var(--iw-text-subtle)' : undefined }}
                >
                  {column.header}
                </span>
              )}
              {column.headerInfo ? <ColumnHeaderInfo label={column.headerInfo.label} content={column.headerInfo.content} /> : null}
              {resizable ? (
                <div
                  className={'iw-grid-resize-handle' + (resize.resizingColumnId === column.id ? ' iw-grid-resize-handle--active' : '')}
                  aria-hidden="true"
                  onMouseDown={(event) => handleResizeHandleMouseDown(event, column)}
                  title={`Resize ${column.header} (or focus the header and press Shift+Left/Right)`}
                  data-width={width}
                />
              ) : null}
            </div>
          )
        })}
        {hasActions ? (
          <div
            role="columnheader"
            aria-colindex={totalCols}
            className="iw-grid-header-cell iw-grid-cell--pinned-right"
            style={{ width: ACTIONS_COL_WIDTH, right: 0 }}
          >
            <span className="iw-visually-hidden">Row actions</span>
          </div>
        ) : null}
      </div>

      {shouldVirtualize ? (
        // `role="rowgroup"` (Verifier F3): without it, this spacer/positioning wrapper sits
        // between `role="grid"` and the `role="row"` children, which trips axe's
        // `aria-required-children` (a `row`'s only valid parents are `grid`/`treegrid`/`rowgroup`/
        // `table`). `rowgroup` preserves grid ownership of the windowed rows exactly like a
        // `<tbody>` groups `<tr>`s under a `<table>` while a separate header row sits alongside it.
        <div role="rowgroup" style={{ position: 'relative', height: virtualizer.getTotalSize(), minWidth: 'max-content' }}>
          {virtualizer.getVirtualItems().map((virtualRow) =>
            renderRow(rows[virtualRow.index]!, virtualRow.index, {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              transform: `translateY(${virtualRow.start}px)`,
            }),
          )}
        </div>
      ) : (
        rows.map((row, rowIndex) => renderRow(row, rowIndex))
      )}
    </div>
  )
}
